/**
 * Shift Tic Tac Toe - Core Game Logic
 * Pure functions for game state management
 */

// Winning combinations (indices)
const WIN_LINES = [
  [0, 1, 2], // top row
  [3, 4, 5], // middle row
  [6, 7, 8], // bottom row
  [0, 3, 6], // left column
  [1, 4, 7], // middle column
  [2, 5, 8], // right column
  [0, 4, 8], // diagonal top-left to bottom-right
  [2, 4, 6], // diagonal top-right to bottom-left
];

// Adjacent cells for each position (for shift phase)
const ADJACENTS = {
  orthogonal: {
    0: [1, 3],
    1: [0, 2, 4],
    2: [1, 5],
    3: [0, 4, 6],
    4: [1, 3, 5, 7],
    5: [2, 4, 8],
    6: [3, 7],
    7: [4, 6, 8],
    8: [5, 7],
  },
  diagonal: {
    0: [1, 3, 4],
    1: [0, 2, 3, 4, 5],
    2: [1, 4, 5],
    3: [0, 1, 4, 6, 7],
    4: [0, 1, 2, 3, 5, 6, 7, 8],
    5: [1, 2, 4, 7, 8],
    6: [3, 4, 7],
    7: [3, 4, 5, 6, 8],
    8: [4, 5, 7],
  },
};

/**
 * Create initial game state
 * @param {Object} config - Game configuration
 * @returns {Object} Initial game state
 */
export function createInitialState(config = {}) {
  const {
    maxPieces = 4,
    allowDiagonal = true,
    startingPlayer = 'X',
  } = config;

  return {
    board: Array(9).fill(null),
    currentPlayer: startingPlayer,
    phase: 'placement',
    piecesPlaced: { X: 0, O: 0 },
    maxPieces: { X: maxPieces, O: maxPieces },
    winner: null,
    winningLine: null,
    isDraw: false,
    selectedPiece: null,
    allowDiagonal,
    moveHistory: [],
    lastMove: null,
    lastPlacedPosition: { X: null, O: null },
    canShiftThisTurn: false,
  };
}

/**
 * Check if there's a winner
 * @param {Array} board - Current board state
 * @returns {Object|null} { winner: 'X'|'O', line: [indices] } or null
 */
export function checkWinner(board) {
  for (const line of WIN_LINES) {
    const [a, b, c] = line;
    if (board[a] && board[a] === board[b] && board[a] === board[c]) {
      return { winner: board[a], line };
    }
  }
  return null;
}

/**
 * Get all valid placement positions
 * @param {Array} board - Current board state
 * @returns {Array} Array of valid position indices
 */
export function getValidPlacements(board) {
  return board.reduce((acc, cell, index) => {
    if (cell === null) acc.push(index);
    return acc;
  }, []);
}

/**
 * Get adjacent cells for a position
 * @param {number} position - Cell index
 * @param {boolean} allowDiagonal - Allow diagonal moves
 * @returns {Array} Array of adjacent indices
 */
export function getAdjacentCells(position, allowDiagonal = true) {
  const adjacentMap = allowDiagonal ? ADJACENTS.diagonal : ADJACENTS.orthogonal;
  return adjacentMap[position] || [];
}

/**
 * Get valid shift destinations for a piece (any empty cell on the board)
 * @param {Array} board - Current board state
 * @param {number} fromPosition - Position of piece to move
 * @returns {Array} Array of valid destination indices (all empty cells)
 */
export function getValidShiftDestinations(board, fromPosition) {
  if (board[fromPosition] === null) return [];
  
  // Return all empty cells as valid destinations
  const emptyCells = [];
  board.forEach((cell, index) => {
    if (cell === null) {
      emptyCells.push(index);
    }
  });
  return emptyCells;
}

/**
 * Get all valid shifts for the current player (only their own pieces).
 * @param {Object} state - Current game state
 * @returns {Array} Array of { from, to, piece } objects
 */
export function getValidShifts(state) {
  const { board, currentPlayer } = state;
  const shifts = [];

  board.forEach((cell, index) => {
    if (cell === null) return;
    // Only allow shifting own pieces
    if (cell !== currentPlayer) return;
    const destinations = getValidShiftDestinations(board, index);
    destinations.forEach((to) => {
      shifts.push({ from: index, to, piece: cell });
    });
  });

  return shifts;
}

/**
 * Check if game should transition to shift phase
 * @param {Object} state - Current game state
 * @returns {boolean}
 */
export function shouldTransitionToShift(state) {
  const { piecesPlaced, maxPieces, phase } = state;
  if (phase !== 'placement') return false;
  
  return (
    piecesPlaced.X >= maxPieces.X &&
    piecesPlaced.O >= maxPieces.O
  );
}

/**
 * Check if the game is a draw
 * @param {Object} state - Current game state
 * @returns {boolean}
 */
export function checkDraw(state) {
  const { phase, winner } = state;

  if (winner) return false;

  // Shift phase continues until someone wins (no stalemate draw)
  if (phase === 'shift') return false;

  return getValidPlacements(state.board).length === 0;
}

/**
 * Whether a player has placed all their pieces
 */
export function hasPlacedAllPieces(state, player) {
  return state.piecesPlaced[player] >= state.maxPieces[player];
}

/**
 * Whether shift phase is active (all pieces on board, no winner yet)
 */
export function isShiftPhase(state) {
  return state.phase === 'shift' && !state.winner && !state.isDraw;
}

/**
 * Place a piece on the board
 * @param {Object} state - Current game state
 * @param {number} position - Position to place piece
 * @param {string} player - 'X' or 'O'
 * @returns {Object} New game state
 */
export function placePiece(state, position, player) {
  const { board, phase, piecesPlaced, maxPieces, currentPlayer } = state;
  
  // Validate
  if (phase !== 'placement') {
    return { ...state, error: 'Not in placement phase' };
  }
  if (player !== currentPlayer) {
    return { ...state, error: 'Not your turn' };
  }
  if (board[position] !== null) {
    return { ...state, error: 'Cell already occupied' };
  }
  if (piecesPlaced[player] >= maxPieces[player]) {
    return { ...state, error: 'No pieces left to place' };
  }
  
  // Create new board
  const newBoard = [...board];
  newBoard[position] = player;
  
  // Update pieces placed
  const newPiecesPlaced = {
    ...piecesPlaced,
    [player]: piecesPlaced[player] + 1,
  };
  
  // Check for winner
  const winResult = checkWinner(newBoard);
  
  const enteringShift = shouldTransitionToShift({
    ...state,
    board: newBoard,
    piecesPlaced: newPiecesPlaced,
  });

  // Normal alternation — after the last placement, the other player shifts first
  const nextPlayer = player === 'X' ? 'O' : 'X';

  let newState = {
    ...state,
    board: newBoard,
    piecesPlaced: newPiecesPlaced,
    currentPlayer: nextPlayer,
    lastPlacedPosition: {
      ...state.lastPlacedPosition,
      [player]: position,
    },
    lastMove: { type: 'place', position, player },
    moveHistory: [...state.moveHistory, { type: 'place', position, player }],
    phase: enteringShift ? 'shift' : state.phase,
    canShiftThisTurn: enteringShift,
    error: null,
  };

  if (winResult) {
    newState.winner = winResult.winner;
    newState.winningLine = winResult.line;
    newState.canShiftThisTurn = false;
  } else if (checkDraw(newState)) {
    newState.isDraw = true;
  }

  return resolveBlockedShiftTurn(newState);
}

/**
 * Select a piece for shifting
 * @param {Object} state - Current game state
 * @param {number} position - Position of piece to select
 * @param {string} player - 'X' or 'O'
 * @returns {Object} New game state
 */
export function selectPiece(state, position, player) {
  const { board, phase, currentPlayer } = state;
  
  if (phase !== 'shift') {
    return { ...state, error: 'Not in shift phase' };
  }
  if (player !== currentPlayer) {
    return { ...state, error: 'Not your turn' };
  }
  if (board[position] === null) {
    return { ...state, error: 'No piece at this cell' };
  }

  // Check if piece can move (any empty cell is valid)
  const destinations = getValidShiftDestinations(board, position);
  if (destinations.length === 0) {
    return { ...state, error: 'No empty cells to move to' };
  }
  
  return {
    ...state,
    selectedPiece: position,
    error: null,
  };
}

/**
 * Deselect the currently selected piece
 * @param {Object} state - Current game state
 * @returns {Object} New game state
 */
export function deselectPiece(state) {
  return {
    ...state,
    selectedPiece: null,
    error: null,
  };
}

/**
 * Shift a piece to a new position
 * @param {Object} state - Current game state
 * @param {number} from - Position to move from
 * @param {number} to - Position to move to
 * @param {string} player - 'X' or 'O'
 * @returns {Object} New game state
 */
export function shiftPiece(state, from, to, player) {
  const { board, phase, currentPlayer } = state;
  
  // Validate
  if (phase !== 'shift') {
    return { ...state, error: 'Not in shift phase' };
  }
  if (player !== currentPlayer) {
    return { ...state, error: 'Not your turn' };
  }
  const piece = board[from];
  if (piece === null) {
    return { ...state, error: 'No piece to move' };
  }
  // Can only move your own pieces
  if (piece !== player) {
    return { ...state, error: 'Not your piece' };
  }
  if (board[to] !== null) {
    return { ...state, error: 'Destination is occupied' };
  }

  // Create new board
  const newBoard = [...board];
  newBoard[from] = null;
  newBoard[to] = piece;
  
  // Check for winner
  const winResult = checkWinner(newBoard);
  
  // One shift per turn — pass to opponent after each move
  let newState = {
    ...state,
    board: newBoard,
    currentPlayer: player === 'X' ? 'O' : 'X',
    selectedPiece: null,
    lastMove: { type: 'shift', from, to, player, piece },
    moveHistory: [...state.moveHistory, { type: 'shift', from, to, player, piece }],
    lastPlacedPosition: {
      ...state.lastPlacedPosition,
      [piece]: to,
    },
    canShiftThisTurn: false,
    error: null,
  };

  if (winResult) {
    newState.winner = winResult.winner;
    newState.winningLine = winResult.line;
  }

  return resolveBlockedShiftTurn(newState);
}

/**
 * End shift turn — pass to opponent (after unlimited shifts on current turn)
 */
export function endShiftTurn(state, player) {
  const { phase, currentPlayer } = state;

  if (phase !== 'shift') {
    return { ...state, error: 'Not in shift phase' };
  }
  if (player !== currentPlayer) {
    return { ...state, error: 'Not your turn' };
  }

  return resolveBlockedShiftTurn({
    ...state,
    currentPlayer: player === 'X' ? 'O' : 'X',
    selectedPiece: null,
    canShiftThisTurn: false,
    error: null,
  });
}

/**
 * Whether a player has at least one legal shift on the current board.
 */
export function canPlayerShift(state, player) {
  if (state.currentPlayer !== player) return false;
  return getValidShifts(state).length > 0;
}

/**
 * Pass the turn when the current player cannot reach any empty cell.
 * If neither player can shift, the game is a draw.
 */
export function resolveBlockedShiftTurn(state) {
  const { phase, winner, isDraw, currentPlayer } = state;

  if (phase !== 'shift' || winner || isDraw) {
    return state;
  }

  if (canPlayerShift(state, currentPlayer)) {
    return state;
  }

  const opponent = getOpponent(currentPlayer);
  const skipped = {
    ...state,
    currentPlayer: opponent,
    selectedPiece: null,
    error: null,
  };

  if (canPlayerShift(skipped, opponent)) {
    return skipped;
  }

  return { ...skipped, isDraw: true };
}

/**
 * Check if a move is valid
 * @param {Object} state - Current game state
 * @param {Object} move - Move to validate
 * @param {string} player - 'X' or 'O'
 * @returns {boolean}
 */
export function isValidMove(state, move, player) {
  const { phase, board, currentPlayer, piecesPlaced, maxPieces } = state;
  
  if (player !== currentPlayer) return false;
  if (state.winner || state.isDraw) return false;
  
  if (phase === 'placement') {
    if (move.type !== 'place') return false;
    if (board[move.position] !== null) return false;
    if (piecesPlaced[player] >= maxPieces[player]) return false;
    return true;
  }
  
  if (phase === 'shift') {
    if (move.type !== 'shift') return false;
    if (board[move.from] === null) return false;
    // Can only move your own pieces
    if (board[move.from] !== player) return false;
    if (board[move.to] !== null) return false;
    return true;
  }
  
  return false;
}

/**
 * Get the opponent player
 * @param {string} player - 'X' or 'O'
 * @returns {string}
 */
export function getOpponent(player) {
  return player === 'X' ? 'O' : 'X';
}

/**
 * Clone the game state (deep copy)
 * @param {Object} state - Current game state
 * @returns {Object} Cloned state
 */
export function cloneState(state) {
  return {
    ...state,
    board: [...state.board],
    piecesPlaced: { ...state.piecesPlaced },
    maxPieces: { ...state.maxPieces },
    moveHistory: [...state.moveHistory],
    winningLine: state.winningLine ? [...state.winningLine] : null,
  };
}

/**
 * Get game status text
 * @param {Object} state - Current game state
 * @returns {string}
 */
export function getGameStatus(state) {
  if (state.winner) {
    return `${state.winner} wins!`;
  }
  if (state.isDraw) {
    return "It's a draw!";
  }
  if (state.phase === 'placement') {
    return `${state.currentPlayer}'s turn to place`;
  }
  if (state.selectedPiece !== null) {
    return `${state.currentPlayer}: Select destination`;
  }
  return `${state.currentPlayer}'s turn to move`;
}

export { WIN_LINES, ADJACENTS };
